/**
 * Insurance selection screen
 *
 * Route: `/webAppOnboarding/insurance-select`. Patient picks an insurer and confirms it with "Weiter"; only the
 * confirmation persists the choice to `signedUpPatients` and navigates to the matching explain route.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard`.
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local: `useSaveOnboardingStep('insurance_select')`; no billing-type guard (this screen is the fallback).
 */

import { PATIENT_TRACKING_EVENTS } from '@physiofit-digital/physiofit_types';
import type { Insurance } from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '../../../styles/colors';
import { fontFamily, space, text } from '../../../styles/onboardingUiTokens';
import { InsuranceSelect } from '../components/content/InsuranceSelect';
import { prefetchOnboardingHero } from '../components/content/assets/womanPlankImage';
import { SelectedInsuranceButton } from '../components/content/SelectedInsuranceButton';
import { Notice } from '../components/elements/Notice';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { Body } from '../components/typography/Body';
import { H1 } from '../components/typography/HeadingLevels';
import { companyConfig } from '../config/companyConfig';
import { useBnplCohort } from '../hooks/useBnplCohort';
import { useCourseCertified } from '../hooks/useCourseCertified';
import { useSelectedInsurance } from '../hooks/useSelectedInsurance';
import { displayInsuranceName } from '../insurance/displayInsuranceName';
import { buildWebAppOnboardingInsuranceInfo, resolveEffectiveBillingType } from '../insurance/metaBuilders';
import { getCourseNotCertifiedPath, resolveInsuranceOnboardingContext } from '../insurance/routing';
import { insuranceListName } from '../insurance/searchInsurances';
import type { WebAppOnboardingFirestorePatch } from '../services/editSignedUpPatient';
import { editSignedUpPatient } from '../services/editSignedUpPatient';
import { clearDisplayedBillingInfoIfNotInvoiceCheckout, useSaveOnboardingStep } from '../services/onboardingProgress';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

const PreSelectedBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-block: ${space.lg} ${space.xxl};
`;

const HelpLink = styled.a`
  width: fit-content;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.caption.fontSize};
  line-height: ${text.caption.lineHeight};
  font-weight: 500;
  color: ${colors.medium};
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${colors.dark};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

function InsuranceNotFoundLink() {
  return (
    <HelpLink href={companyConfig.support.insuranceNotFoundArticleUrl} target="_blank" rel="noreferrer">
      Ich finde meine Versicherung nicht.
    </HelpLink>
  );
}

export function WebOnboardingInsuranceSelectScreen() {
  useSaveOnboardingStep('insurance_select');
  const navigate = useNavigate();
  const posthog = usePostHog();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const inviteContext = useOnboardingStore((state) => state.inviteContext);
  const inviteContextStatus = useOnboardingStore((state) => state.inviteContextStatus);
  // The practice-fee flag rides on the invite context, so confirming an insurer before it lands would
  // route a private patient to the free screen. Blocks only while it can still arrive; a failed
  // fetch is never retried, so waiting on it would strand the patient (PF-5963).
  const inviteContextLoading = inviteContextStatus === 'idle' || inviteContextStatus === 'loading';
  const { insurance: catalogInsurance } = useSelectedInsurance();
  const cohortResult = useBnplCohort();
  const cohortLoading = cohortResult.status === 'loading';
  const [pending, setPending] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();
  const [isChangingInsurance, setIsChangingInsurance] = useState(false);
  const [pickedInsurance, setPickedInsurance] = useState<Insurance | null>(null);

  const ctx = useMemo(
    () => resolveInsuranceOnboardingContext({ source: 'patient', patient: signedUpPatient }),
    [signedUpPatient],
  );

  const courseId = signedUpPatient?.course?.id ?? '';
  const certified = useCourseCertified(courseId);
  const certifiedReady = certified.status !== 'loading';
  // error → treat as certified (normal flow)
  const courseNotCertified = certified.status === 'ready' && certified.certified === false;

  // The not-certified screen tells the patient the course costs them nothing, which is wrong when the
  // practice charges its own fee, so the fee screen outranks it (PF-5963).
  const resolveForwardTarget = useCallback(
    (explainPath: string | null, practiceChargesFee: boolean): string | null =>
      courseNotCertified && !practiceChargesFee ? getCourseNotCertifiedPath() : explainPath,
    [courseNotCertified],
  );

  const persistedPracticeChargesFee = signedUpPatient?.webAppOnboarding?.insuranceInfo?.practiceChargesFee === true;

  const hasPersistedInsurance = ctx.billingType != null;
  const hasSelection = (pickedInsurance !== null || hasPersistedInsurance) && !isChangingInsurance;

  const insuranceDisplayName = useMemo(() => {
    if (ctx.insuranceName?.trim()) {
      return ctx.insuranceName.trim();
    }
    if (catalogInsurance) {
      return displayInsuranceName(catalogInsurance);
    }
    return '';
  }, [ctx.insuranceName, catalogInsurance]);

  // The hero first appears one screen later, on every billing path. Fetching it here spends idle
  // time the patient is already using to pick an insurer, so the next screen paints with it.
  useEffect(prefetchOnboardingHero, []);

  const handleBack = useCallback(() => {
    navigate('/webAppOnboarding/phone-verification');
  }, [navigate]);

  const confirmSelection = useCallback(
    async (insurance: Insurance) => {
      setSaveError(undefined);
      setPending(true);
      try {
        const displayName = displayInsuranceName(insurance);
        // Resolved ONCE here: the cohort decision is practice × insurer, evaluated live at
        // selection time, and both the routing context and the persisted record below use this
        // same value so they can never disagree.
        const effectiveBillingType = resolveEffectiveBillingType(
          insurance,
          signedUpPatient ?? undefined,
          cohortResult.status === 'ready' ? cohortResult.cohort : null,
        );
        const insuranceInfo = buildWebAppOnboardingInsuranceInfo(insurance, displayName, effectiveBillingType, {
          practiceChargesFee: inviteContext?.practiceChargesPrivateFee === true,
        });
        const selectCtx = resolveInsuranceOnboardingContext({
          source: 'insurance',
          insurance,
          billingType: effectiveBillingType,
          practiceChargesFee: insuranceInfo.practiceChargesFee === true,
        });
        const patch: WebAppOnboardingFirestorePatch = {
          'webAppOnboarding.insuranceInfo': insuranceInfo,
        };
        posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_select_completed, {
          insuranceId: insurance.id,
          billingType: selectCtx.billingType,
          billingBlockedReason: selectCtx.meta.blockedExplain,
          // Set to User
          $set: {
            insuranceId: insurance.id,
            billingType: selectCtx.billingType,
            billingBlockedReason: selectCtx.meta.blockedExplain,
          },
        });
        await editSignedUpPatient(patch);
        const patient = useOnboardingStore.getState().signedUpPatient;
        const webAppOnboarding = patient?.webAppOnboarding;
        if (patient && webAppOnboarding) {
          useOnboardingStore.getState().setSignedUpPatient({
            ...patient,
            webAppOnboarding: { ...webAppOnboarding, insuranceInfo },
          });
        }
        clearDisplayedBillingInfoIfNotInvoiceCheckout(insurance, effectiveBillingType);
        const target = resolveForwardTarget(selectCtx.explainPath, insuranceInfo.practiceChargesFee === true);
        if (target) {
          navigate(target);
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[onboarding] insurance select save failed', error);
        }
        setSaveError('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
      } finally {
        setPending(false);
      }
    },
    [navigate, posthog, resolveForwardTarget, signedUpPatient, cohortResult, inviteContext],
  );

  // A tap only picks: a mis-tap while scrolling the list must not save an insurer and leave the screen.
  const handleSelect = useCallback((insurance: Insurance) => {
    setSaveError(undefined);
    setPickedInsurance(insurance);
    setIsChangingInsurance(false);
    // Closes the mobile keyboard, which would otherwise hide the footer's "Weiter".
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  const handleForward = useCallback(() => {
    if (pickedInsurance) {
      void confirmSelection(pickedInsurance);
      return;
    }
    const target = resolveForwardTarget(ctx.explainPath, persistedPracticeChargesFee);
    if (target) {
      navigate(target);
    }
  }, [pickedInsurance, confirmSelection, navigate, ctx.explainPath, resolveForwardTarget, persistedPracticeChargesFee]);

  return (
    <OnboardingLayout
      currentStep={3}
      totalSteps={6}
      onBack={handleBack}
      onForward={handleForward}
      forwardDisabled={pending || !hasSelection || !certifiedReady || cohortLoading || inviteContextLoading}
      forwardBusy={pending}
    >
      <OnboardingHeader />
      <OnboardingBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm, marginBottom: space.md }}>
          <H1>Bei welcher Krankenkasse bist du versichert?</H1>
          <Body tone="muted" variant="document">
            Dein Physiofit Gesundheitskurs ist von allen Krankenkassen zertifiziert, so dass die Kosten vollständig
            übernommen werden.
          </Body>
          <InsuranceNotFoundLink />
        </div>
        {saveError ? (
          <Notice preset="warning" title="Speichern fehlgeschlagen">
            {saveError}
          </Notice>
        ) : null}
        {hasSelection ? (
          <PreSelectedBlock>
            <SelectedInsuranceButton
              // Fresh pick: the tapped row, "(privat)" included, to check before "Weiter". Saved pick: its stored name.
              insuranceName={pickedInsurance ? insuranceListName(pickedInsurance) : insuranceDisplayName || '—'}
              onChangeRequest={() => setIsChangingInsurance(true)}
              disabled={pending}
            />
          </PreSelectedBlock>
        ) : (
          <InsuranceSelect
            onSelect={handleSelect}
            disabled={!certifiedReady || cohortLoading || inviteContextLoading}
            title=""
            searchPlaceholder="z.B. AOK, TK, DAK, ..."
            searchLabel="Krankenkasse suchen"
            emptyResultAction={<InsuranceNotFoundLink />}
          />
        )}
      </OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}
